import type { ReactDOM } from 'react'
import { useCallback, useContext, useMemo } from 'react'

import { useTranslations } from 'next-intl'

import { indexes } from '@mab-common/utils'

import classnames from 'classnames'

import { Col, ContainerTypes, Grid, Row } from '~/components/atoms/Grid'
import ArticleAuthor from '~/components/blocks/ArticleAuthor/ArticleAuthor'
import { modalTypes } from '~/components/modalLoader'
import { GlobalStoreContext } from '~/contexts/GlobalStoreContext'
import { GlobalStoreProps } from '~/contexts/GlobalStoreReducer'
import type {
  TSearchAnalystEventParams,
  TSearchAnalystEventProps,
} from '~/services/elastic/analyst'
import useSearchAnalyst, {
  SearchAnalystEventNames,
  SearchAnalystMethods,
} from '~/services/elastic/analyst'
import { useMediaQuery } from '~/services/useMediaQuery'
import bp from '~/styles/breakpoints/breakpoints'
import { multiPostHogEventPusher } from '~/utils/analyticsPusher'
import { toURL } from '~/utils/urlResolver'

import Button, { ButtonVariants } from '../Button/Button'
import { Icon } from '../Icon/Icon'
import type { ImageProps } from '../Image/Image'
import Image from '../Image/Image'
import Link from '../Link/Link'
import type { PDPValuationCtaProps } from '../PDPValuationCta/PDPValuationCta'
import type { SummaryProps } from '../Summary/Summary'
import Summary from '../Summary/Summary'
import Text, { TextSizes } from '../Text/Text'
import VideoWatchTime from '../VideoWatchTime/VideoWatchTime'

import styles from './ArticlePreviewCard.module.css'
import EsIndexTypes = indexes.EsIndexTypes

export interface ArticlePreviewCardProps {
  _id: string
  title: string | null
  articleType: string | null
  articleTypeSlug: string | null
  subtitle?: string | null
  category?: { name: string }[] | string
  modules?: SummaryProps['modules'] | any
  summary?: string | null
  summaryLimit?: SummaryProps['limit']
  hideSummary?: boolean
  readtime?: string | null
  variant?:
    | 'default'
    | 'small'
    | 'medium'
    | 'large'
    | 'huge'
    | 'magazine'
    | 'magazineResponsive'
    | 'search'
  imageType?: 'square' | 'landscape' | 'portrait' | 'midlandscape'
  orientation?: 'portrait' | 'landscape'
  slug: string
  artistSlug?: string | null
  artistName?: string | null
  listPreviewImage?: ImageProps
  leadImage?: ImageProps
  className?: {
    base?: string
    imageContainer?: string
    detailContainer?: string
  }
  [key: string]: any
  testID?: string
  sizes?: string
  noPrefetch?: boolean
  imageCredit?: string
  titleTag?: keyof ReactDOM
  noMarginBottom?: boolean
  itemIndex?: number
  insightsQueryID?: string
  searchInsightsEvent?: TSearchAnalystEventProps
  sansTitle?: boolean
  showReadMore?: boolean
  hideAuthor?: boolean
  author?: any
  secondaryCategories?:
    | {
        name: string
        slug: string
      }[]
    | null
  expert?: PDPValuationCtaProps['expert'] | null
  relatedArtist?: {
    expert: PDPValuationCtaProps['expert']
  }
  publishDate?: string
  handleAnalytics?: () => void
  testAttr?: string
  openWatchModal?: boolean
  video?: {
    _createdAt: string
    _id: string
    _updatedAt: string
    coverImage: {
      _type: string
      asset: {
        _ref: any
        _type: any
      }
    }
    summary: string | any
    title: string
    videoUrl: string
  }
  type?: string
  hideDetails?: boolean
  showPlayCircle?: boolean
  videoOnly?: boolean
  showIconLeft?: boolean
  usePrimaryTitle?: boolean
  landscapeThumbnails?: boolean
}

export default function ArticlePreviewCard({
  _id,
  title,
  articleType,
  articleTaxonomy,
  articleTypeSlug,
  modules = [],
  summary,
  summaryLimit = 120,
  hideSummary = false,
  variant = 'default',
  imageType = 'square',
  slug,
  artistSlug,
  artistName,
  leadImage,
  listPreviewImage,
  className,
  testID,
  sizes,
  noPrefetch = false,
  titleTag = 'h2',
  noMarginBottom,
  itemIndex,
  insightsQueryID,
  searchInsightsEvent,
  sansTitle,
  orientation = 'portrait',
  showReadMore = false,
  readtime,
  secondaryCategories,
  expert,
  author,
  hideAuthor = false,
  relatedArtist,
  publishDate,
  handleAnalytics,
  testAttr = 'article-preview-card',
  enforceNoReadMore = false,
  openWatchModal,
  video,
  hideDetails = false,
  showPlayCircle = false,
  videoOnly = false,
  showIconLeft = true,
  usePrimaryTitle,
  landscapeThumbnails = false,
}: ArticlePreviewCardProps) {
  const t = useTranslations('common')
  const isMobile = useMediaQuery(`(max-width: ${bp.bpMedium})`)
  const { stateDispatch } = useContext(GlobalStoreContext)

  const theme = {
    default: {
      title: TextSizes.magSecondaryTitleMed,
      category: TextSizes.magSecondaryTitleMicro,
      summary: TextSizes.bodyMed,
    },
    small: {
      title: TextSizes.magSecondaryTitleXSml,
      category: TextSizes.magSecondaryTitleMicro,
      summary: TextSizes.bodyMed,
    },
    medium: {
      title: TextSizes.magSecondaryTitleSml,
      category: TextSizes.magSecondaryTitleMicro,
      summary: TextSizes.bodyMed,
    },
    search: {
      title: TextSizes.magSecondaryTitleXSml,
      category: TextSizes.magSecondaryTitleMicro,
      summary: TextSizes.bodyMed,
    },
    large: {
      title: sansTitle
        ? TextSizes.primaryTitleLrg
        : TextSizes.magSecondaryTitleLrg,
      category: TextSizes.magSecondaryTitleMicro,
      summary: TextSizes.bodyMed,
    },
    huge: {
      title: sansTitle
        ? TextSizes.primaryTitleLrg
        : TextSizes.magSecondaryTitleMed,
      category: TextSizes.magSecondaryTitleMicro,
      summary: TextSizes.bodyMed,
    },
    magazine: {
      title: TextSizes.magSecondaryTitleMed,
      category: TextSizes.magSecondaryTitleMicro,
      summary: TextSizes.bodyMed,
    },
    magazineResponsive: {
      title: TextSizes.magSecondaryTitleResponsive,
      category: TextSizes.magSecondaryTitleMicro,
      summary: TextSizes.bodyMed,
    },
  }

  const moduleSummary = summary ? summary : modules

  const urlObj: any = {
    slug: slug,
    articleTaxonomy,
    _type: 'article',
  }

  if (articleType) {
    urlObj.parent = {
      _type: articleType,
      slug: artistSlug ? artistSlug : articleTypeSlug,
    }
  }

  const { sendSearchInsightsEvent } = useSearchAnalyst()

  const searchInsightsEventProps: TSearchAnalystEventProps = useMemo(() => {
    return (
      searchInsightsEvent || {
        method: SearchAnalystMethods.clickedObjectIDs,
        name: SearchAnalystEventNames.footerArticleClicked,
      }
    )
  }, [])

  const searchInsightsEventParams: TSearchAnalystEventParams = useMemo(() => {
    const args = {
      indexType: EsIndexTypes.ARTICLES,
      eventName: searchInsightsEventProps.name as any,
      objectIDs: [_id],
    }
    if (
      searchInsightsEventProps.method ===
        SearchAnalystMethods.clickedObjectIDsAfterSearch &&
      insightsQueryID
    ) {
      return {
        ...args,
        queryID: insightsQueryID,
        ...(itemIndex ? { positions: [itemIndex] } : {}),
      }
    }
    return args
  }, [searchInsightsEventProps, _id, insightsQueryID, itemIndex])

  const onArticleClick = useCallback(async () => {
    sendSearchInsightsEvent(
      searchInsightsEventProps.method,
      searchInsightsEventParams
    )

    await multiPostHogEventPusher([
      {
        eventType: `${title} article clicked`,
      },
    ])

    if (handleAnalytics) {
      handleAnalytics()
    }
  }, [searchInsightsEventProps.method, title, searchInsightsEventParams])

  const openVideoModal = useCallback(
    (e?: any) => {
      if (e) {
        e.preventDefault()
      }

      // Map video object properties to WatchVideoModal props
      const modalProps = {
        videoUrl: video?.videoUrl,
        title: video?.title || title,
        description: video?.summary,
        image: video?.coverImage,
        author: author,
        hideHeader: true,
      }

      stateDispatch({
        type: GlobalStoreProps.UPDATE_MODAL_STATE,
        payload: {
          isOpen: true,
          component: modalTypes.watchVideoModal,
          content: null,
          props: modalProps,
        },
      })

      // Still track analytics
      onArticleClick()
    },
    [video, title, author, stateDispatch, onArticleClick]
  )

  const isVideo = ['video', 'videos'].includes(articleTypeSlug || '')
  const isPodcast = ['podcast', 'podcasts'].includes(articleTypeSlug || '')
  const isPodcastOrVideo = isVideo || isPodcast

  if (isPodcastOrVideo) {
    hideSummary = false
    showReadMore = true
    if (enforceNoReadMore) {
      showReadMore = false
    }
  }

  const renderDetails = () => {
    const authorDetails = hideAuthor ? undefined : author

    if (hideDetails) return null

    return (
      <div className={classnames([styles[`${orientation}DetailsContainer`]])}>
        <div className={classnames([styles.detailContainer])}>
          {/* Useful for debugging category */}
          {/* {articleTypeSlug}
          {secondaryCategories?.map((cat, i) => {
            return <div key={`cat-${i}`}>secondary {cat.name}</div>
          })} */}
          {artistName && (
            <Text
              size={TextSizes.primaryTitleSml}
              className={styles.artistTitle}
            >
              {artistName}
            </Text>
          )}
          {openWatchModal && isVideo ? (
            <a href="#" onClick={openVideoModal}>
              <Text
                Tag={titleTag}
                size={
                  usePrimaryTitle
                    ? TextSizes.primaryTitleLrg
                    : theme[variant].title
                }
                className={styles.title}
              >
                {title}
              </Text>
            </a>
          ) : (
            <Link href={toURL(urlObj)} onClick={onArticleClick} noPrefetch>
              <Text
                Tag={titleTag}
                size={
                  usePrimaryTitle
                    ? TextSizes.primaryTitleLrg
                    : theme[variant].title
                }
                className={styles.title}
              >
                {title}
              </Text>
            </Link>
          )}
          {(!isPodcastOrVideo || (isPodcastOrVideo && authorDetails?.name)) && (
            <ArticleAuthor
              author={authorDetails}
              centered={false}
              preText={isPodcastOrVideo ? t('featuring') : t('writtenBy')}
              size={variant === 'search' && isMobile ? 'small' : undefined}
            />
          )}

          {!hideSummary && (
            <>
              {openWatchModal && isVideo ? (
                <a href="#" onClick={openVideoModal}>
                  <Text
                    size={theme[variant].summary}
                    className={styles.summary}
                  >
                    <Summary modules={moduleSummary} limit={summaryLimit} />
                  </Text>
                </a>
              ) : (
                <Link href={toURL(urlObj)} onClick={onArticleClick} noPrefetch>
                  <Text
                    size={theme[variant].summary}
                    className={styles.summary}
                  >
                    <Summary modules={moduleSummary} limit={summaryLimit} />
                  </Text>
                </Link>
              )}
            </>
          )}
          {(variant === 'large' ||
            variant === 'huge' ||
            variant === 'magazineResponsive' ||
            showReadMore) &&
            renderReadMoreButton()}
        </div>
      </div>
    )
  }

  const renderReadMoreButton = () => {
    let buttonText = t('readMore')
    if (isVideo) {
      buttonText = t('watchVideo')
    }
    if (isPodcast) {
      buttonText = t('listenToPodcast')
    }

    const shouldOpenModal = openWatchModal && isVideo

    if (orientation === 'landscape' || hideSummary) {
      return (
        <div
          className={classnames(styles.readmore, styles.readMoreBtnLandscape)}
        >
          <Button
            link={shouldOpenModal ? undefined : toURL(urlObj)}
            linkOnclick={
              shouldOpenModal
                ? e => {
                    e.preventDefault()
                    openVideoModal()
                  }
                : undefined
            }
            variant={ButtonVariants.textLinkSmall}
            className={styles.readMoreText}
            iconLeft={
              isPodcast ? (
                <svg
                  width="16"
                  height="17"
                  viewBox="0 0 16 17"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M8 16.5C12.4183 16.5 16 12.9183 16 8.5C16 4.08172 12.4183 0.5 8 0.5C3.58172 0.5 0 4.08172 0 8.5C0 12.9183 3.58172 16.5 8 16.5ZM6.07585 5.6468C6 5.74467 6 5.91999 6 6.27062V10.7294C6 11.08 6 11.2553 6.07585 11.3532C6.14194 11.4385 6.24311 11.4919 6.35312 11.4994C6.47935 11.5082 6.632 11.4134 6.93729 11.2237L10.5269 8.99437C10.7918 8.82984 10.9243 8.74757 10.97 8.64297C11.01 8.55157 11.01 8.44843 10.97 8.35703C10.9243 8.25243 10.7918 8.17016 10.5269 8.00563L6.93729 5.77625C6.632 5.58664 6.47935 5.49184 6.35312 5.50055C6.24311 5.50814 6.14194 5.5615 6.07585 5.6468Z"
                    fill="#222222"
                  />
                </svg>
              ) : (
                showIconLeft
              )
            }
          >
            {buttonText}
          </Button>
        </div>
      )
    }

    return (
      <div
        className={classnames(styles.readMoreBtn, {
          [styles.isPodcastOrVideo]: isPodcastOrVideo,
        })}
      >
        <Button
          link={shouldOpenModal ? undefined : toURL(urlObj)}
          linkOnclick={shouldOpenModal ? openVideoModal : undefined}
          variant={ButtonVariants.capsUnderlineSml}
          iconLeft={!isPodcastOrVideo || showIconLeft}
          className={styles.readMoreBtn}
        >
          {buttonText}
        </Button>
      </div>
    )
  }

  const renderImage = () => {
    const cardImage = listPreviewImage || leadImage
    const imageContent = (
      <>
        {isPodcast ? (
          <div
            className={classnames(styles.podcastImageContainer, {
              [styles.landscapePodcast]: landscapeThumbnails,
            })}
          >
            {cardImage?.asset && (
              <Image
                {...cardImage}
                sizes={sizes}
                alt={title ? title : undefined}
                objectFit="contain"
              />
            )}
            <div className={styles.podcastPlayButton}>
              <Icon type="PodcastPlayButton" />
            </div>
          </div>
        ) : (
          <div
            className={classnames(
              styles.imageContainer,
              className?.imageContainer,
              imageType && styles[`imageContainer${imageType}`],
              {
                [styles.landscapePodcast]:
                  orientation === 'landscape' && isPodcast,
                [styles.darkened]: isVideo && videoOnly,
              }
            )}
          >
            {cardImage?.asset && (
              <Image
                {...cardImage}
                sizes={sizes}
                alt={title ? title : undefined}
              />
            )}

            {showPlayCircle && isVideo && (
              <div className={styles.videoPlayCircle}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="80"
                  height="80"
                  viewBox="0 0 80 80"
                  fill="none"
                >
                  <circle cx="40" cy="40" r="40" fill="#DDDDDD" />
                  <path
                    d="M34 32.5687C34 31.4 34 30.8156 34.2579 30.4893C34.4826 30.205 34.8266 30.0271 35.2006 30.0018C35.6298 29.9728 36.1488 30.2888 37.1868 30.9208L49.3914 38.3521C50.2922 38.9005 50.7425 39.1748 50.8981 39.5234C51.034 39.8281 51.034 40.1719 50.8981 40.4766C50.7425 40.8252 50.2922 41.0995 49.3914 41.6479L37.1868 49.0792C36.1488 49.7112 35.6298 50.0272 35.2006 49.9982C34.8266 49.9729 34.4826 49.795 34.2579 49.5107C34 49.1844 34 48.6 34 47.4313V32.5687Z"
                    fill="#222222"
                  />
                </svg>
              </div>
            )}

            {isVideo && readtime && (
              <div
                className={classnames(styles.watchTimeContainer, {
                  [styles.landscape]: orientation === 'landscape',
                })}
              >
                <VideoWatchTime watchTime={readtime} />
              </div>
            )}
          </div>
        )}
      </>
    )

    if (openWatchModal && isVideo) {
      return (
        <a href="#" className={styles.imageLink} onClick={openVideoModal}>
          {imageContent}
        </a>
      )
    }

    return (
      <Link
        href={toURL(urlObj)}
        className={styles.imageLink}
        noPrefetch={noPrefetch}
        onClick={onArticleClick}
      >
        {imageContent}
      </Link>
    )
  }

  return (
    <div
      className={classnames(
        styles.base,
        className?.base,
        styles[variant],
        styles[orientation],
        noMarginBottom && styles.basenoMarginBottom
      )}
      data-testid={testID}
      data-test={testAttr}
    >
      {orientation === 'landscape' ? (
        <Grid variant={ContainerTypes.NoGutters}>
          <Row>
            {isMobile && (
              <Col
                variant={
                  isMobile && variant === 'search' ? 'col-1/4' : 'col-1/2--md'
                }
              >
                {renderImage()}
              </Col>
            )}
            <Col
              variant={
                isMobile && variant === 'search' ? 'col-3/4' : 'col-1/2--md'
              }
            >
              {renderDetails()}
            </Col>
            {!isMobile && <Col variant="col-1/2--md">{renderImage()}</Col>}
          </Row>
        </Grid>
      ) : (
        <>
          {renderImage()}
          {renderDetails()}
        </>
      )}
    </div>
  )
}
